import { useAuth } from './use-auth';
import axios, { AxiosError, AxiosInstance, AxiosResponse } from 'axios';
import { Configuration } from 'oas';
import * as React from 'react';
import { useSnackbar } from 'shared';
import { setupDatasharingMock } from 'api/datasharing/__mocks__/datasharing-mock';
import { API_NAMES } from 'api/constants';

const getApiBasePath = (apiName?: string): string | undefined => {
    if (!apiName) {
        return undefined;
    }

    const apiUrlMap: Record<string, string | undefined> = {
        [API_NAMES.DATASHARING]: process.env.REACT_APP_DATASHARING_API_URL,
        [API_NAMES.ACTIVITY_SERVICE]: process.env.REACT_APP_ACTIVITY_SERVICE_API_URL,
    };

    return apiUrlMap[apiName];
};

export const useApiConfig = (
    apiVersion: string,
    apiName?: string,
): { apiConfig: Configuration; axiosInstance: AxiosInstance } => {
    const basePath = getApiBasePath(apiName) || process.env.REACT_APP_API_HOST + '/' + apiVersion;
    const { errorSnackbar } = useSnackbar();

    const auth = useAuth();

    // Datasharing is mocked in dev, so it doesnt need a JWT
    // Other external APIs may need a JWT though
    const skipAccessToken = apiName === API_NAMES.DATASHARING && process.env.REACT_APP_ENVIRONMENT === 'dev';

    const apiConfig = new Configuration({
        basePath,
        accessToken: apiName && !skipAccessToken ? async () => await auth.getAccessToken() : undefined,
    });

    const axiosInstance: AxiosInstance = React.useMemo(() => {
        const newAxiosInstance = axios.create();

        // setup mocks for external APIs in dev env
        if (process.env.REACT_APP_ENVIRONMENT === 'dev' && apiName) {
            const accountId = auth.user?.customer_id;
            setupDatasharingMock(newAxiosInstance, accountId);
        }

        if (auth.user) {
            newAxiosInstance.interceptors.response.use(
                (response: AxiosResponse) => response,
                async (error: AxiosError) => {
                    if (error.response) {
                        const status = error.response?.status;

                        // eslint-disable-next-line no-console
                        console.debug('interceptor status', status);

                        if (status === 401) {
                            auth.logout();
                        } else if (status >= 500 && status <= 599) {
                            const message = error.response.data.message;

                            // errors from the zendesk auth can be sporadic (due to 429's or 500's)
                            // but they are not critical, therefore, we do not want to notify the user of this
                            if (error.response.config.url?.includes('/api/v6/zendesk/auth')) {
                                return;
                            }

                            errorSnackbar(message || 'Unknown error');
                        }
                    }

                    return error;
                },
            );
        }

        return newAxiosInstance;
    }, [auth, errorSnackbar, apiName]);

    return { apiConfig, axiosInstance };
};
